import { api } from './api';
import type { EvalCase, EvalType, EvalToolCall, PaginatedEvalCaseResponse, EvalTool } from '../types';

interface ApiResponse<T> {
  success: boolean;
  code: number;
  message: string;
  data: T;
}

// Required: caseCode, name, type. appId is create-only.
export interface EvalCaseCreateRequest {
  caseCode: string;
  name: string;
  type: EvalType[];
  caseType?: string | null;
  description?: string | null;
  version?: number | null;
  input?: unknown;
  expectedResult?: unknown;
  tools?: EvalTool[] | null;
  expectedToolCalls?: EvalToolCall[] | null;
  expectedSources?: (string | { sourceRef: string })[] | null;
  /** Ground truth the case carries, independent of retrieval. */
  context?: string[] | null;
  modelId?: string | null;
  tags?: string[] | null;
  appId?: string;
}

// Update mirrors create minus caseCode/appId semantics (appId immutable).
export type EvalCaseUpdateRequest = Partial<Omit<EvalCaseCreateRequest, 'appId'>>;

// BE /eval-cases/list query params (snake_case, matches FastAPI Query names).
export interface EvalCaseListParams {
  page?: number;
  page_size?: number;
  search?: string;
  include_deleted?: boolean;
  type?: string;
  tag?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  filters?: string;
  app_id?: string;
  /** Comma-separated case ids: fetch exactly these. */
  ids?: string;
  include_global?: boolean; // also include core/global rows (Show core toggle)
}

export const evalCasesService = {
  list: async (params?: EvalCaseListParams): Promise<PaginatedEvalCaseResponse> => {
    const response = await api.get<PaginatedEvalCaseResponse>('/api/v1/eval-cases/list', { params });
    return response.data;
  },

  getById: async (id: string): Promise<EvalCase> => {
    const response = await api.get<ApiResponse<EvalCase>>(`/api/v1/eval-cases/get/${id}`);
    return response.data.data;
  },

  create: async (data: EvalCaseCreateRequest): Promise<EvalCase> => {
    const response = await api.post<ApiResponse<EvalCase>>('/api/v1/eval-cases/create', data);
    return response.data.data;
  },

  update: async (id: string, data: EvalCaseUpdateRequest): Promise<EvalCase> => {
    const response = await api.put<ApiResponse<EvalCase>>(`/api/v1/eval-cases/update/${id}`, data);
    return response.data.data;
  },

  softDelete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete<ApiResponse<{ message: string }>>(`/api/v1/eval-cases/delete/${id}`);
    return response.data.data;
  },

  restore: async (id: string): Promise<{ message: string }> => {
    const response = await api.patch<ApiResponse<{ message: string }>>(`/api/v1/eval-cases/restore/${id}`);
    return response.data.data;
  },
};
